/**
 * Spec 01 §3's core sixteen menu items — now seventeen, `admin-permissions` inserted
 * beside `policies`/`audit-log`, see that item's own note — followed by every module
 * added since (recruiting, HR, payroll, geo, outreach, sourcing, inbox, presence,
 * effectiveness, people, roster — thirty-five in total as of Task T2-E-web), and the
 * projection that decides which of them a session sees.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THE MENU IS NOT A SECURITY BOUNDARY.
 *
 *  API_CONTRACT.md §7, first paragraph: "Frontend ẩn nút **không** được coi là
 *  kiểm quyền" — hiding a button is not a permission check. Every request is
 *  authorised on the server by the seven-step chain of 02 §11 (`resolveScope` in
 *  `src/lib/scope.ts`), on every call, whatever this file renders.
 *
 *  So what is this for? Honesty, not enforcement. A menu that offers a screen the
 *  API will refuse teaches people to expect refusals; a menu that hides a screen
 *  the API would allow hides work someone is entitled to do. Both are usability
 *  defects, and neither is a vulnerability.
 *
 *  Two things follow, and they are the reason this comment is here:
 *
 *   1. **Do not "fix" a scope bug by editing this file.** If a screen shows data it
 *      should not, the defect is in the chain, and removing the menu item hides the
 *      symptom while the endpoint stays open to anyone who types the URL.
 *   2. **Do not add a role check here.** The input below is
 *      `SessionUser.permissions` — the *effective* set the server computed, role
 *      floor plus live `allow` rows minus live `deny` rows (Task 15). Re-deriving
 *      it from `role` in the browser would be a second authorisation path, drifting
 *      from the first the moment a grant row is written. This build has already had
 *      to correct one such path; it does not need a second.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Each item names the permission requirement of the endpoint that feeds it, copied
 * from `API_CONTRACT.md` §7.2. `requires` is an **anyOf**: the item shows when the
 * effective set intersects it. An item with an empty `requires` needs a session and
 * nothing more.
 */

import type { MessageKey } from '../i18n/messages';
import type { DashboardPermission } from '../api/types';
import type { IconName } from '../states/icons';

/**
 * The seven headings the rail is folded into (UI-13).
 *
 * Forty-two items is taller than a 900px window, so the item you are standing on
 * could be below the fold on a rail that — by *Luật Cạnh Bàn Đứng Yên* — is not
 * allowed to scroll itself back. Folding is the way out that keeps both rules:
 * `DESIGN.md` §12 *Luật Rail Gọn*.
 *
 * The membership below is the module structure this file already documents in its
 * own section comments, not a new taxonomy: the recruiting block, the HR block, the
 * payroll block, the governance cluster `admin-permissions` names, and so on. Two
 * items sit where their own notes put them rather than where the label first
 * suggests — `geo` ("a check-in is always the caller's own") and `presence` (the
 * `workforce.view_self` floor) are an employee's daily surfaces, not operations.
 */
export type MenuGroupId =
  | 'daily'
  | 'people'
  | 'recruiting'
  | 'payroll'
  | 'operations'
  | 'analytics'
  | 'admin';

export interface MenuItem {
  readonly id: string;
  /**
   * The 16px glyph beside the label on the rail — the world's own spec for a
   * navigation item (`Work-Management-Refactored/DESIGN.md`, "Navigation").
   *
   * A hint, never the label: every item still carries its own words, so a reader
   * who does not recognise a silhouette has lost nothing. Items reuse glyphs on
   * purpose — sixteen shapes for forty-two screens is the whole set the icon file
   * draws, and a distinct blob per screen would be forty-two things to learn.
   */
  readonly icon: IconName;
  readonly path: string;
  readonly labelKey: MessageKey;
  /**
   * The rail heading this item folds under. Required, so an item added later cannot
   * compile without an answer — a rail that fits only until the next module lands is
   * not a rail that fits.
   */
  readonly group: MenuGroupId;
  /** anyOf — at least one of these must be in the effective set. */
  readonly requires: readonly DashboardPermission[];
  /** The task that builds this screen's contents. WP-K1 ships the route and the frame. */
  readonly task: string;
  /** Why this requirement and not another; only where §7.2 needed interpreting. */
  readonly note?: string;
}

export const MENU_ITEMS: readonly MenuItem[] = [
  {
    id: 'overview',
    icon: 'calendar',
    path: '/overview',
    labelKey: 'nav.overview',
    group: 'daily',
    requires: ['workforce.view_department', 'workforce.view_workspace'],
    task: 'WP-K4',
    note:
      'Epic 7 executive/manager dashboard. Its subject is a team, so `view_self` alone does not ' +
      'reach it; an employee reads their own numbers on My Activity (spec 01 §17).',
  },
  {
    id: 'live-activity',
    icon: 'activity',
    path: '/live-activity',
    labelKey: 'nav.liveActivity',
    group: 'daily',
    requires: ['workforce.view_department', 'workforce.view_workspace'],
    task: 'WP-K4',
    note: 'Same as Overview: `GET /activities/timeline` at team width.',
  },
  {
    id: 'attendance',
    icon: 'calendar',
    path: '/attendance',
    labelKey: 'nav.attendance',
    group: 'daily',
    requires: ['workforce.view_self', 'workforce.view_department', 'workforce.view_workspace'],
    task: 'WP-K3',
    note:
      '`GET /attendance`, scope `resolved`. One endpoint, three fields of view — an employee ' +
      'reaches their own days here, which is the third of the three items spec 01 §3 gives them.',
  },
  {
    id: 'time-tracking',
    icon: 'clock',
    path: '/time-tracking',
    labelKey: 'nav.timeTracking',
    group: 'daily',
    requires: ['workforce.view_self', 'workforce.view_department', 'workforce.view_workspace'],
    task: 'WP-K2',
    note: '`GET /time-entries` (resolved) and `POST /timers/*` (self).',
  },
  {
    id: 'productivity',
    icon: 'bar-chart',
    path: '/productivity',
    labelKey: 'nav.productivity',
    group: 'analytics',
    requires: ['workforce.view_department', 'workforce.view_workspace'],
    task: 'WP-K4',
    note:
      'The team productivity screen. `GET /productivity/scores` also accepts `view_self`, but an ' +
      "employee's own score, its components and its drivers live on My Activity with the " +
      'explanation and the correction path (spec 01 §11, §17). Listing it here on `view_self` ' +
      "would put a fourth item in the employee's menu and contradict spec 01 §3.",
  },
  {
    id: 'productivity-formulas',
    icon: 'bar-chart',
    path: '/productivity/formulas',
    labelKey: 'nav.productivityFormulas',
    group: 'analytics',
    requires: ['workforce.manage_policies'],
    task: 'U17',
    note:
      'GET/POST /productivity/formulas, PATCH and activate /productivity/formulas/{id}, ' +
      'scope company (`formulas/authorizer.ts`) — a THIRD item on ' +
      '`workforce.manage_policies`, because that is the code the server asks for and ' +
      'this task adds none (KTD4). NOT `view_department`/`view_workspace`, which the ' +
      '`productivity` item above uses: reading a team’s scores and rewriting the ' +
      'arithmetic those scores are computed with are different powers, and the ' +
      'endpoints already say so. Placed immediately after `productivity` for the ' +
      'reason `alert-rules` sits after `alerts` and `policy-collection` after ' +
      '`policies`: this is the configuration that decides what that screen shows, and ' +
      'a reader looking for one will look where the other is. Until Tier-3 U17 the ' +
      'six WP-J1 endpoints, the nine-component vocabulary and the immutability ' +
      'trigger reached nothing a browser could open.',
  },
  {
    id: 'applications',
    icon: 'list',
    path: '/applications',
    labelKey: 'nav.applications',
    group: 'operations',
    requires: ['workforce.view_apps'],
    task: 'WP-K4',
  },
  {
    id: 'websites',
    icon: 'list',
    path: '/websites',
    labelKey: 'nav.websites',
    group: 'operations',
    requires: ['workforce.view_websites'],
    task: 'WP-K4',
  },
  {
    id: 'devices',
    icon: 'cpu',
    path: '/devices',
    labelKey: 'nav.devices',
    group: 'operations',
    requires: ['workforce.manage_devices'],
    task: 'WP-K5',
  },
  {
    id: 'alerts',
    icon: 'bell',
    path: '/alerts',
    labelKey: 'nav.alerts',
    group: 'operations',
    requires: [
      'workforce.resolve_incidents',
      'workforce.view_department',
      'workforce.view_workspace',
    ],
    task: 'WP-L5',
    note: '`GET /alerts` — sensitive access; the step-6 gate still runs server-side.',
  },
  {
    id: 'alert-rules',
    icon: 'bell',
    path: '/alert-rules',
    labelKey: 'nav.alertRules',
    group: 'operations',
    requires: ['workforce.manage_alerts'],
    task: 'U16',
    note:
      'GET/POST /alert-rules and PATCH /alert-rules/{id}, scope company — the FIRST ' +
      'menu item on `workforce.manage_alerts`. Until Tier-3 U16 that code was in the ' +
      'matrix, gated three live endpoints, and reached nothing a browser could open: ' +
      'the only way to change what a company is watched for was a database client. ' +
      'Its own code and NOT the `alerts` item’s read set: `manage_alerts` was ' +
      'deliberately removed from `GET /alerts` (SECURITY_THREAT_MODEL §8.2), so the ' +
      'person who writes the rules is not thereby the person who reads who they fired ' +
      'about. Placed immediately after `alerts` because this is the catalogue that ' +
      'decides what that screen shows, the same way `policy-collection` sits after ' +
      '`policies`.',
  },
  {
    id: 'incidents',
    icon: 'triangle-alert',
    path: '/incidents',
    labelKey: 'nav.incidents',
    group: 'operations',
    requires: ['workforce.resolve_incidents'],
    task: 'WP-L5',
  },
  {
    id: 'data-risk',
    icon: 'shield',
    path: '/data-risk',
    labelKey: 'nav.dataRisk',
    group: 'operations',
    requires: ['workforce.view_usb_events', 'workforce.view_file_events'],
    task: 'WP-L5',
    note:
      'API_CONTRACT §7.3: these two have no v1 endpoint of their own and gate sensitive data at ' +
      'step 6 instead. They are still the right requirement for the menu item — the screen is ' +
      'about exactly that data — and the enum keeps them for this reason.',
  },
  {
    id: 'reports',
    icon: 'file',
    path: '/reports',
    labelKey: 'nav.reports',
    group: 'analytics',
    requires: ['workforce.export_reports'],
    task: 'WP-K8',
  },
  {
    id: 'ai-insights',
    icon: 'bot',
    path: '/ai-insights',
    labelKey: 'nav.aiInsights',
    group: 'analytics',
    requires: ['workforce.use_ai_analytics'],
    task: 'WP-M4',
  },
  {
    id: 'policies',
    icon: 'shield',
    path: '/policies',
    labelKey: 'nav.policies',
    group: 'admin',
    requires: ['workforce.manage_policies'],
    task: 'WP-K5',
  },
  {
    id: 'policy-collection',
    icon: 'shield',
    path: '/policies/collection',
    labelKey: 'nav.policyCollection',
    group: 'admin',
    requires: ['workforce.manage_policies'],
    task: 'U15',
    note:
      'GET/POST /policies, PATCH and activate /policies/{id}, scope company — the ' +
      'same single requirement `policies` uses, because it is the same code the ' +
      'server asks for. A SECOND item on `workforce.manage_policies` rather than a ' +
      'section of `policies`: that screen is the website catalogue and its ' +
      'read-only posture panel, and this one is the collection policy itself — what ' +
      'is captured from a named person machine, which until Tier-3 U15 could only ' +
      'be changed with a database client. Placed immediately after `policies` ' +
      'because a reader looking for one will look where the other is. The rollout ' +
      'count on it needs no menu requirement of its own; the per-device half needs ' +
      '`workforce.manage_devices` and the screen asks for it separately.',
  },
  {
    id: 'company-settings',
    icon: 'sliders-horizontal',
    path: '/settings',
    labelKey: 'nav.companySettings',
    group: 'admin',
    requires: ['workforce.manage_policies'],
    task: 'CS-2',
    note:
      'GET/PUT /company/settings, scope company on the write. The FOURTH item on ' +
      '`workforce.manage_policies`, placed immediately after `policy-collection` ' +
      'for the reason that whole cluster is ordered: this is configuration that ' +
      'decides what other screens show, and a reader looking for one will look ' +
      'where the others are. The READ side of this surface sits on ' +
      '`workforce.view_self` instead — everybody needs the currency code to read ' +
      'their own payslip, and the payroll panels call it directly rather than ' +
      'through this item. The menu asks for the write code because opening this ' +
      'screen is going there to change the two values.',
  },
  {
    id: 'admin-permissions',
    icon: 'shield',
    path: '/admin/permissions',
    labelKey: 'nav.adminPermissions',
    group: 'admin',
    requires: ['workforce.manage_policies'],
    task: 'T2-D',
    note:
      'GET/POST/DELETE /admin/permission-grants, scope company. The same single ' +
      "requirement `policies` uses — Task 15's permission-grants API had zero web " +
      'callers until this task, the biggest reachability gap the pillars-deepening ' +
      'program named. Placed beside `policies` and ahead of `audit-log` rather than ' +
      'appended after `people` the way every module since recruiting has been: this ' +
      'item is not a new vocabulary or a new module, it is the missing write surface ' +
      'for a permission `policies` already gates, and belongs in the same governance ' +
      'cluster on screen as it does in `src/lib/permissions.ts`.',
  },
  {
    id: 'admin-retention',
    icon: 'shield',
    path: '/admin/retention',
    labelKey: 'nav.retention',
    group: 'admin',
    requires: ['workforce.manage_retention'],
    task: 'U11',
    note:
      'GET/PUT /admin/retention, scope company. The FIRST menu item gated on ' +
      '`workforce.manage_retention` — until Task U11 that code gated a job rather ' +
      'than an endpoint, and no screen could name it. Its own code and not ' +
      '`manage_policies` (the code `policies` and `admin-permissions` share): a ' +
      'company that grants somebody the collection policy is not thereby granting ' +
      'them the power to shorten the audit trail, and the server asks for exactly ' +
      'this code. Placed in the same governance cluster on screen as it sits in ' +
      '`src/lib/permissions.ts` — beside `admin-permissions` and ahead of ' +
      '`audit-log`, whose contents this screen decides the lifetime of.',
  },
  {
    id: 'admin-dsar',
    icon: 'shield',
    path: '/admin/dsar',
    labelKey: 'nav.dsarQueue',
    group: 'admin',
    requires: ['hr.manage_employee_documents'],
    task: 'U10',
    note:
      'GET /dsar/requests and POST /dsar/requests/{id}/execute, scope company. Gated on ' +
      '`hr.manage_employee_documents` — the code that already governs an employee’s ' +
      'identity documents, and no new vocabulary (KTD4): if you may upload somebody’s ' +
      'passport scan you are the same role that executes their erasure. NOT ' +
      '`workforce.manage_retention`, which decides how long a CLASS of data lives for the ' +
      'whole company and is a different question from acting on ONE person’s record. ' +
      'Placed in the governance cluster beside `admin-retention` and ahead of `audit-log`, ' +
      'whose rows this screen’s erasures deliberately do not touch.',
  },
  {
    id: 'audit-log',
    icon: 'list',
    path: '/audit-log',
    labelKey: 'nav.auditLog',
    group: 'admin',
    requires: ['workforce.view_audit_logs'],
    task: 'WP-K8',
  },
  {
    id: 'my-activity',
    icon: 'activity',
    path: '/my-activity',
    labelKey: 'nav.myActivity',
    group: 'daily',
    requires: ['workforce.view_self'],
    task: 'WP-K2',
    note: 'Spec 01 §17 — the employee transparency surface. Everyone with a session holds this.',
  },
  {
    id: 'my-data',
    icon: 'user',
    path: '/my-data',
    labelKey: 'nav.myData',
    group: 'daily',
    requires: ['workforce.view_self'],
    task: 'U8',
    note:
      'Tier-3 U8 — the PDPL (Law 91/2025/QH15) right to a copy of one’s own data. The same ' +
      'anyOf as My Activity, and for the same reason: the floor every session holds, because ' +
      'the subject of a DSAR export is the session itself. No role check and no new code — ' +
      'a manager sees THEIR OWN requests here, not their team’s.',
  },

  // ── Recruiting (Phase 1 ATS) ────────────────────────────────────────────────
  //
  // A distinct module rather than more of spec 01 §3's sixteen, so it is grouped
  // after the core items and gated on the `recruiting.*` vocabulary. Each item names
  // the requirement of the endpoint that feeds it, copied from `openapi.yaml`'s
  // `x-wp-permissions`. Only Jobs is built this task (WP-W1); the other four get a
  // route and a placeholder so nav and routing work without a 404 — later tasks fill
  // them. The `requires` are still an **anyOf** and still not a role check.
  {
    id: 'recruiting-jobs',
    icon: 'briefcase',
    path: '/recruiting/jobs',
    labelKey: 'nav.recruitingJobs',
    group: 'recruiting',
    requires: ['recruiting.view_jobs'],
    task: 'WP-W1',
    note: '`GET /recruiting/requisitions` — `recruiting.view_jobs` at scope company.',
  },
  {
    id: 'candidates',
    icon: 'users',
    path: '/recruiting/candidates',
    labelKey: 'nav.candidates',
    group: 'recruiting',
    requires: ['recruiting.view_candidates'],
    task: 'WP-W2',
  },
  {
    id: 'pipeline',
    icon: 'briefcase',
    path: '/recruiting/pipeline',
    labelKey: 'nav.pipeline',
    group: 'recruiting',
    requires: ['recruiting.view_candidates'],
    task: 'WP-W2',
    note:
      'The pipeline is a view of candidates against a requisition, so it reads on ' +
      '`recruiting.view_candidates` like the candidates list it draws from.',
  },
  {
    id: 'interviews',
    icon: 'calendar',
    path: '/recruiting/interviews',
    labelKey: 'nav.interviews',
    group: 'recruiting',
    requires: ['recruiting.submit_feedback', 'recruiting.view_candidates'],
    task: 'WP-W3',
    note:
      'anyOf: an interviewer holds `submit_feedback` to file a scorecard, and a ' +
      'coordinator with `view_candidates` reads the schedule — either reaches the screen.',
  },
  {
    id: 'offers',
    icon: 'file',
    path: '/recruiting/offers',
    labelKey: 'nav.offers',
    group: 'recruiting',
    requires: ['recruiting.manage_offers'],
    task: 'WP-W3',
  },

  // ── HR / people-ops (Phase 3a) ──────────────────────────────────────────────
  //
  // A third distinct module, grouped after recruiting and gated on the `hr.*`
  // vocabulary. Each item names the KIND requirement of the endpoints that feed it,
  // copied from `openapi.yaml`'s `x-wp-permissions`; the WHOSE half (a `workforce.view_*`
  // code over the employee tree) is resolved server-side on every call and is not a menu
  // concern. The `requires` are still an **anyOf** and still not a role check.
  {
    id: 'hr-documents',
    icon: 'file',
    path: '/hr/documents',
    labelKey: 'nav.hrDocuments',
    group: 'people',
    requires: ['hr.view_employee_hr'],
    task: 'WP-W6',
    note:
      '`GET /hr/employees/{id}/documents` and `…/contracts` — `hr.view_employee_hr` is the ' +
      'KIND gate. Reading a document’s bytes needs the stronger `hr.view_employee_documents`, ' +
      'enforced at the download endpoint, not here.',
  },
  {
    id: 'onboarding',
    icon: 'users',
    path: '/hr/onboarding',
    labelKey: 'nav.onboarding',
    group: 'people',
    requires: ['hr.manage_onboarding', 'hr.view_employee_hr'],
    task: 'WP-W6',
    note:
      'anyOf: an HR admin holds `hr.manage_onboarding` to run onboarding, and a reader with ' +
      '`hr.view_employee_hr` can list templates and an employee’s checklists — either reaches ' +
      'the screen. The write actions are still authorised server-side on `manage_onboarding`.',
  },
  {
    id: 'hr-leave',
    icon: 'calendar',
    path: '/hr/leave',
    labelKey: 'nav.hrLeave',
    group: 'people',
    requires: ['hr.request_leave', 'hr.view_leave'],
    task: 'WP-W7',
    note:
      'anyOf, and deliberately no role check: `hr.request_leave` is the self-service floor ' +
      '`src/lib/hiring-permissions.ts` gives every role before any grant row, so this item ' +
      'shows for an employee who has never been granted anything. `hr.view_leave` reaches it ' +
      'too, for a reader who can see leave but not file it. Approving (`hr.approve_leave`) ' +
      'needs no menu entry of its own — it is a section inside this same screen.',
  },
  {
    id: 'hr-performance',
    icon: 'bar-chart',
    path: '/hr/performance',
    labelKey: 'nav.hrPerformance',
    group: 'people',
    requires: ['hr.view_reviews', 'hr.view_own_review'],
    task: 'WP-W8',
    note:
      'anyOf, and deliberately no role check: `hr.view_own_review` is the self-service floor ' +
      '`src/lib/hiring-permissions.ts` gives every role before any grant row, so this item ' +
      'shows for an employee who has never been granted anything. `hr.view_reviews` reaches ' +
      'it too, for a reviewer/HR admin who reads team reviews. Managing cycles/goals ' +
      '(`hr.manage_review_cycles`) and submitting a review (`hr.submit_review`) need no menu ' +
      'entry of their own — they are sections inside this same screen.',
  },

  // ── Payroll (Task 16, Phase 8a) ─────────────────────────────────────────────
  //
  // A fourth distinct module, grouped after HR and gated on the `payroll.*`
  // vocabulary. `requires` names the KIND requirement of the endpoints that feed it,
  // copied from `src/lib/hiring-permissions.ts`'s `PAYROLL_PERMISSIONS` — including
  // the run-items endpoint, which since ruling P1 (roadmap 2026-09-13) is
  // company-wide on that KIND code alone: no `workforce.view_*`/employee-tree half
  // remains for this menu to account for.
  {
    id: 'payroll',
    icon: 'wallet',
    path: '/payroll',
    labelKey: 'nav.payroll',
    group: 'payroll',
    requires: ['payroll.view_own_payslip', 'payroll.view_payroll'],
    task: 'WP-W9',
    note:
      'anyOf, and deliberately no role check: `payroll.view_own_payslip` is the self-service ' +
      'floor `src/lib/hiring-permissions.ts` gives every role before any grant row, so this ' +
      'item shows for an employee who has never been granted anything — they still reach My ' +
      'payslip. `payroll.view_payroll` reaches it too, for someone who reads runs/periods but ' +
      'has no payslip of their own yet. Managing the template (`payroll.manage_templates`), ' +
      'creating/calculating a run (`payroll.run_payroll`) and the two-step approval ' +
      '(`payroll.approve_payroll`, `payroll.ceo_approve`) need no menu entry of their own — ' +
      'they are sections inside this same screen.',
  },

  // ── Geo check-in (Task 17 API, WP-W10, Phase 7 SPA) ─────────────────────────
  //
  // A fifth distinct module, grouped after payroll — but gated on `workforce.view_self`
  // itself rather than a `geo.*` vocabulary of its own: `openapi.yaml` puts the geo
  // endpoints' `x-wp-permissions` on the *workforce* codes (`POST /geo/checkin` and
  // `GET /geo/office-locations` are both `workforce.view_self` at scope `self`), so
  // there is no separate enum for `menu.test.ts`'s "names only permissions that exist"
  // check to widen. That single requirement is also why a session holding every
  // `workforce.*` permission sees this item where it sees none of the recruiting, HR
  // or payroll ones — the fact `menu.test.ts` states explicitly rather than leaving as
  // an accident of which list it checked against.
  {
    id: 'geo',
    icon: 'map-pin',
    path: '/geo',
    labelKey: 'nav.geo',
    group: 'daily',
    requires: ['workforce.view_self'],
    task: 'WP-W10',
    note:
      'Deliberately no role check, the same reason `my-activity` needs none: a check-in ' +
      "is always the caller's own, so `workforce.view_self` — the floor every role holds " +
      '— is the whole requirement. Team check-ins (`workforce.view_department` / ' +
      '`workforce.view_workspace`) and office-location management ' +
      '(`workforce.manage_policies`) need no menu entry of their own — they are sections ' +
      "inside this same screen, client-gated exactly as payroll's approval steps are.",
  },

  // ── Outreach (Task 18 API, WP-W11, Phase 5 SPA) ─────────────────────────────
  //
  // A sixth distinct module, grouped after geo. The compliance core of candidate
  // outreach — channel policy, messages, consent and suppression — gated on
  // `recruiting.manage_candidates`, the same code the candidates surface uses; that
  // is what every message/consent/suppression write needs, and what this item's
  // `requires` is. Channel policy itself is a stricter, `workforce.manage_policies`
  // admin surface (the same code office-location management uses) and needs no menu
  // entry of its own — it is a section inside this same screen, gated the way geo's
  // own office-locations section is.
  {
    id: 'outreach',
    icon: 'users',
    path: '/recruiting/outreach',
    labelKey: 'nav.outreach',
    group: 'recruiting',
    requires: ['recruiting.manage_candidates'],
    task: 'WP-W11',
  },

  // ── Sourcing (Task 20 API, WP-W12, Phase 6 SPA) ─────────────────────────────
  //
  // A seventh distinct module, grouped after outreach. Listing runs is
  // `recruiting.view_candidates` — the same code the candidates surface uses for
  // its reads, because a run lists the leads it found, which are candidate-shaped
  // — and that is what this item's own `requires` is. Triggering a run is a
  // stricter, `recruiting.manage_jobs` write (the same code the requisitions
  // surface uses) and needs no menu entry of its own — it is a section inside
  // this same screen, gated the way outreach's own channel-policy section is.
  {
    id: 'sourcing',
    icon: 'users',
    path: '/recruiting/sourcing',
    labelKey: 'nav.sourcing',
    group: 'recruiting',
    requires: ['recruiting.view_candidates'],
    task: 'WP-W12',
  },

  // ── Inbox (Task 23 API, WP-W13, Phase 4c SPA) ───────────────────────────────
  //
  // An eighth distinct module, grouped after sourcing. The list and the detail read
  // `recruiting.view_candidates` — the same code the candidates surface uses for its
  // reads, and what this item's own `requires` is. The one write-shaped action on the
  // screen, reading an attachment's bytes, is a stricter, sensitive-access read
  // gated on `recruiting.view_candidate_documents` (the same code the candidate-CV
  // download uses) and needs no menu entry of its own — it is a control inside this
  // same screen, rendered for everyone who reaches it and refused server-side for a
  // session that lacks the stronger code, exactly as `CandidateDocumentsPanel`'s own
  // download button is.
  {
    id: 'inbox',
    icon: 'inbox',
    path: '/recruiting/inbox',
    labelKey: 'nav.inbox',
    group: 'recruiting',
    requires: ['recruiting.view_candidates'],
    task: 'WP-W13',
  },

  // ── Presence (Task P2 API, PW1 pillar web) ──────────────────────────────────
  //
  // A ninth distinct module by position — grouped last — but gated on the *core*
  // workforce vocabulary rather than one of its own, exactly the reason `geo`
  // gives for its own item: `openapi.yaml` puts `listPresence`'s and
  // `getPresenceSummary`'s `x-wp-permissions` on the same three `workforce.view_*`
  // codes `/attendance` uses (the `resolved` scope ladder, self ⊂ department ⊂
  // company), so there is no separate `presence.*` enum for `menu.test.ts`'s
  // "names only permissions that exist" check to widen. This item's `requires` is
  // therefore identical to `attendance`'s own — the same anyOf, the same floor —
  // which is why a plain employee (holding only `workforce.view_self`) reaches
  // this board too, for their own single row: the API's own scope resolution, not
  // a client-side guess, is what narrows a manager's board down to that.
  {
    id: 'presence',
    icon: 'users',
    path: '/presence',
    labelKey: 'nav.presence',
    group: 'daily',
    requires: ['workforce.view_self', 'workforce.view_department', 'workforce.view_workspace'],
    task: 'PW1',
    note:
      '`GET /presence` + `/presence/summary`, scope `resolved` — identical anyOf to ' +
      '`attendance`. A coordination-status board, not a productivity score (research ' +
      'guardrail): no field it reads is a number derived from these facts.',
  },

  // ── Effectiveness (Task P3 API, PW2 pillar web) ─────────────────────────────
  //
  // A tenth distinct module by position — grouped last, after presence — gated on
  // BOTH the HR performance codes and the core workforce vocabulary, transcribed
  // verbatim from `openapi.yaml`'s `x-wp-permissions` on `GET /effectiveness` and
  // `GET /effectiveness/team`: `[hr.view_reviews, hr.view_own_review,
  // workforce.view_self, workforce.view_department, workforce.view_workspace]`.
  // The server itself is a HYBRID gate (`effectiveness-authorizer.ts`'s own
  // header) — a KIND gate over the SAME two codes `hr-performance`'s own goals
  // read uses, and a WHOSE gate over the SAME three codes `attendance` and
  // `presence` use — so this item's anyOf is the union of both, and deliberately
  // no role check: `workforce.view_self` alone is enough, so every employee
  // reaches their own goals + score + attendance here, the same reason `geo` and
  // `presence` need no role check for their own floor.
  {
    id: 'effectiveness',
    icon: 'bar-chart',
    path: '/effectiveness',
    labelKey: 'nav.effectiveness',
    group: 'analytics',
    requires: [
      'hr.view_reviews',
      'hr.view_own_review',
      'workforce.view_self',
      'workforce.view_department',
      'workforce.view_workspace',
    ],
    task: 'PW2',
    note:
      '`GET /effectiveness` + `/effectiveness/team` — goals PRIMARY, the productivity ' +
      'score secondary context (always with its confidence), the team a distribution ' +
      '(quartiles) for spotting who needs support, never a ranked list. The research ' +
      'guardrail this screen exists to honour: activity/online-time scores are not a ' +
      'valid productivity measure and are harmful when tied to a target.',
  },

  // ── People directory (Task T2-A API, T2-B pillar web, manage-people pillar) ──
  //
  // An eleventh module by position — grouped last, after effectiveness — but gated
  // on the *core* workforce vocabulary rather than one of its own, the identical
  // reason `geo`'s and `presence`'s own notes give: `openapi.yaml` puts
  // `listEmployees`'s, `readEmployee`'s and `listEmployeeDepartments`'s
  // `x-wp-permissions` on the same three `workforce.view_*` codes `/attendance` and
  // `/presence` use (the `resolved` scope ladder, self ⊂ department ⊂ company), so
  // there is no separate `people.*` enum for `menu.test.ts`'s "names only
  // permissions that exist" check to widen. This item's `requires` is therefore
  // identical to `attendance`'s and `presence`'s own — the same anyOf, the same
  // floor — which is why a plain employee (holding only `workforce.view_self`)
  // reaches this directory too, for a one-row table of themself: the API's own
  // scope resolution, not a client-side guess, is what narrows a manager's
  // directory down to that.
  {
    id: 'people',
    icon: 'users',
    path: '/people',
    labelKey: 'nav.people',
    group: 'people',
    requires: ['workforce.view_self', 'workforce.view_department', 'workforce.view_workspace'],
    task: 'T2-B',
    note: 'GET /employees, scope resolved — same anyOf as presence.',
  },

  // ── Roster grid (Task T2-E API, T2-E-web, location-attendance pillar) ───────
  //
  // A twelfth module by position — grouped last, after people — but gated on the
  // *core* workforce vocabulary rather than one of its own, the identical reason
  // `geo`'s, `presence`'s and `people`'s own notes give: `openapi.yaml` puts
  // `readShiftRoster`'s `x-wp-permissions` on the same three `workforce.view_*`
  // codes `/attendance` and `/people` use (the `resolved` scope ladder, self ⊂
  // department ⊂ company), so there is no separate `roster.*` enum for
  // `menu.test.ts`'s "names only permissions that exist" check to widen. This
  // item's `requires` is therefore identical to `attendance`'s, `presence`'s and
  // `people`'s own — the same anyOf, the same floor — which is why a plain
  // employee (holding only `workforce.view_self`) reaches this grid too, for a
  // one-row week of themself: the API's own scope resolution, not a client-side
  // guess, is what narrows a manager's grid down to that. Assigning a shift and
  // ending an assignment need no menu entry of their own — they are the drawer
  // this same screen opens, client-gated on `workforce.manage_shifts` exactly as
  // geo's own office-location management is.
  {
    id: 'roster',
    icon: 'calendar',
    path: '/roster',
    labelKey: 'nav.roster',
    group: 'daily',
    requires: ['workforce.view_self', 'workforce.view_department', 'workforce.view_workspace'],
    task: 'T2-E-web',
    note: 'GET /shifts/roster, scope resolved — same anyOf as attendance.',
  },
];

/**
 * `/ui-states`, the developer/reviewer reference — an item, but **not** one of the
 * menu items.
 *
 * Deliberately outside `MENU_ITEMS`: it is not a product screen, so it must not
 * appear in the rail's list, and `visibleMenuItems` must keep returning the thirty-
 * five. It is a `MenuItem` all the same so the one predicate — `canOpen` — gates
 * the route and the footer link, instead of a second, hand-written permission test
 * in each place drifting from the other.
 *
 * Gated on `workforce.manage_policies` (UI-12), the code every other admin surface
 * asks for. It renders fixture values only and discloses no tenant data, so the
 * requirement is not a confidentiality boundary — it is honesty about who the page
 * is for: an ordinary employee offered a page of specimen error states has been
 * handed someone else's tool.
 */
export const UI_STATES_ITEM: MenuItem = {
  id: 'ui-states',
  icon: 'sliders-horizontal',
  path: '/ui-states',
  labelKey: 'shell.statesReference',
  // Carried only because `MenuItem` requires it: this link renders in the rail's
  // FOOTER, never inside a group, so no heading ever folds it away. `admin` is the
  // honest answer to "which heading would it live under" — the same
  // `workforce.manage_policies` cluster — not a statement about where it renders.
  group: 'admin',
  requires: ['workforce.manage_policies'],
  task: 'UI-12',
};

/**
 * The projection. One `Set` and one `some`, and nothing else — no role, no
 * hierarchy, no defaulting.
 *
 * `requires: []` would mean "session is enough"; no item declares that today, and
 * the branch is written so that an item which later does gets the honest answer
 * instead of being silently hidden by an empty intersection.
 */
export function visibleMenuItems(
  permissions: readonly DashboardPermission[],
  items: readonly MenuItem[] = MENU_ITEMS,
): readonly MenuItem[] {
  const held = new Set<string>(permissions);
  return items.filter(
    (item) => item.requires.length === 0 || item.requires.some((code) => held.has(code)),
  );
}

/**
 * The headings, in rail order. Item order **inside** a heading is `MENU_ITEMS`'
 * own declaration order, so every "placed immediately after …" note above still
 * describes what a reader sees.
 */
export const MENU_GROUPS: readonly { readonly id: MenuGroupId; readonly labelKey: MessageKey }[] = [
  { id: 'daily', labelKey: 'nav.group.daily' },
  { id: 'people', labelKey: 'nav.group.people' },
  { id: 'recruiting', labelKey: 'nav.group.recruiting' },
  { id: 'payroll', labelKey: 'nav.group.payroll' },
  { id: 'operations', labelKey: 'nav.group.operations' },
  { id: 'analytics', labelKey: 'nav.group.analytics' },
  { id: 'admin', labelKey: 'nav.group.admin' },
];

export interface MenuGroupView {
  readonly id: MenuGroupId;
  readonly labelKey: MessageKey;
  readonly items: readonly MenuItem[];
}

/**
 * The projection, folded. A heading whose every item is gated away for this session
 * is **dropped entirely** — an empty heading is a promise of a screen that is not
 * there, which is the same usability defect `visibleMenuItems` exists to avoid.
 */
export function visibleMenuGroups(
  permissions: readonly DashboardPermission[],
  items: readonly MenuItem[] = MENU_ITEMS,
): readonly MenuGroupView[] {
  const visible = visibleMenuItems(permissions, items);
  return MENU_GROUPS.map((group) => ({
    ...group,
    items: visible.filter((item) => item.group === group.id),
  })).filter((group) => group.items.length > 0);
}

/**
 * Which heading is the session standing under?
 *
 * Longest path first, and a prefix match on top of the exact one, because that is
 * what `NavLink` itself does: `/people/42` marks `/people` as the current page, so
 * the heading over `/people` has to open for it too. Returns `null` for a route that
 * is not a menu item at all (`/ui-states`, a not-found): the rail, not this function,
 * knows which headings that session can actually see, and it opens the first of them.
 * A hard-coded `daily` fallback rendered every heading shut for a viewer who holds no
 * `daily` item — the exact seven-shut rail *Luật Rail Gọn* forbids.
 */
export function menuGroupForPath(
  pathname: string,
  items: readonly MenuItem[] = MENU_ITEMS,
): MenuGroupId | null {
  const match = [...items]
    .sort((a, b) => b.path.length - a.path.length)
    .find((item) => pathname === item.path || pathname.startsWith(`${item.path}/`));
  return match?.group ?? null;
}

/** The item owning a path, for the route guard and the placeholder screens. */
export function menuItemForPath(
  path: string,
  items: readonly MenuItem[] = MENU_ITEMS,
): MenuItem | null {
  return items.find((item) => item.path === path) ?? null;
}

/**
 * May this session open this item?
 *
 * The same predicate the menu uses, applied at the route rather than at the link —
 * because `PRODUCT.md` §3 requires that an employee who *types* `/productivity`
 * gets the permission-denied state rather than a redirect. A refusal the user
 * cannot see is a bug report they cannot write.
 */
export function canOpen(item: MenuItem, permissions: readonly DashboardPermission[]): boolean {
  const held = new Set<string>(permissions);
  return item.requires.length === 0 || item.requires.some((code) => held.has(code));
}
